import type {
	GenerateAndPushToGitUsingGetApiArg,
	GenerateAndPushToGitUsingGetApiResponse,
	GenerateSqlPreviewEnvironmentUsingGetApiArg,
	GenerateSqlPreviewEnvironmentUsingGetApiResponse,
	GenerateSqlPreviewUsingGetApiArg,
	GenerateSqlPreviewUsingGetApiResponse,
	GenerateSqlUsingGetApiArg,
	GenerateSqlUsingGetApiResponse,
	GetSystemUsersUsingGetApiArg,
	GetSystemUsersUsingGetApiResponse,
} from './mddApiSliceGenerated'
import { mddApiSliceGenerated } from './mddApiSliceGenerated'
import type { System } from './mddApiSliceGeneratedV2'

type WithBranchId<T> = T & { branchId: number }

export const mddApi = mddApiSliceGenerated
	.enhanceEndpoints({
	addTagTypes: ['Templates', 'Domains', 'SqlPreview'],
	endpoints: {
		createTemplateV1UsingPost: {
			invalidatesTags: ['Templates'],
		},
		getAllTemplatesV1UsingGet: {
			providesTags: ['Templates'],
		},
		updateTemplateV1UsingPut: {
			invalidatesTags: ['Templates'],
		},
		deleteTemplateV1UsingDelete: {
			invalidatesTags: ['Templates'],
		},
		getTemplateV1UsingGet: {
			providesTags: ['Templates'],
		},
		getAllDomainsFilteredBySystemTechnologyDepUsingGet: {
			providesTags: ['Domains'],
		},
		/** Used by Preview tab; invalidated after structure save (e.g. ViewDetailV2) */
		generateSqlPreviewUsingGet: {
			providesTags: (_result, _error, arg) => [
				{ type: 'SqlPreview', id: arg.structureId },
			],
		},
	},
	})
	.injectEndpoints({
		overrideExisting: true,
		endpoints: (build) => ({
			getSystemUsersUsingGet: build.query<
				GetSystemUsersUsingGetApiResponse,
				GetSystemUsersUsingGetApiArg
			>({
				query: (queryArg) => ({
					url: `/v2/branch/${queryArg.branchId}/structure/${queryArg.structureId}/system`,
				}),
				transformResponse: (response: System) => response.systemUsers ?? [],
			}),
			generateSqlPreviewUsingGet: build.query<
				GenerateSqlPreviewUsingGetApiResponse,
				WithBranchId<GenerateSqlPreviewUsingGetApiArg>
			>({
				query: (queryArg) => ({
					url: `/branch/${queryArg.branchId}/structure/${queryArg.structureId}/sql`,
					params: {
						objectSettingsId: queryArg.objectSettingsId,
					},
				}),
				providesTags: (_result, _error, arg) => [
					{ type: 'SqlPreview', id: arg.structureId },
				],
			}),
			generateSqlPreviewEnvironmentUsingGet: build.query<
				GenerateSqlPreviewEnvironmentUsingGetApiResponse,
				WithBranchId<GenerateSqlPreviewEnvironmentUsingGetApiArg>
			>({
				query: (queryArg) => ({
					url: `/branch/${queryArg.branchId}/structure/${queryArg.structureId}/environments/${queryArg.environmentId}/objectTypes/${queryArg.objectSettingsId}/preview`,
				}),
			}),
			generateSqlUsingGet: build.query<
				GenerateSqlUsingGetApiResponse,
				WithBranchId<GenerateSqlUsingGetApiArg>
			>({
				query: (queryArg) => ({
					url: `/branch/${queryArg.branchId}/structure/${queryArg.structureId}/environments/${queryArg.environmentId}/objectTypes/${queryArg.objectSettingsId}/sql`,
				}),
			}),
			generateAndPushToGitUsingGet: build.query<
				GenerateAndPushToGitUsingGetApiResponse,
				WithBranchId<GenerateAndPushToGitUsingGetApiArg>
			>({
				query: (queryArg) => ({
					url: `/branch/${queryArg.branchId}/structure/${queryArg.structureId}/environments/${queryArg.environmentId}/objectTypes/${queryArg.objectSettingsId}/git`,
				}),
			}),
		}),
	})

export const {
	useAddApiUserUsingPostMutation,
	useAdjustTableNameUsingPostMutation,
	useApiUserResetSecretUsingPostMutation,
	useApiUserUpdateUsingPostMutation,
	useChangeReleaseStatusUsingPatchMutation,
	useCheckStructureUsingGetQuery,
	useCreateTemplateV1UsingPostMutation,
	useCreateUsingPostMutation,
	useDeleteApiUserUsingDeleteMutation,
	useDeleteTemplateV1UsingDeleteMutation,
	useGetAllCustomAttributesForComboUsingGetQuery,
	useGetAllCustomAttributesUsingGetQuery,
	useGetAllDomainsFilteredBySystemTechnologyDepUsingGetQuery,
	useGetAllTechnologiesUsingGetQuery,
	useGetAllTemplatesV1UsingGetQuery,
	useGetApiUsersUsingGetQuery,
	useGetAvailableStructureForSourceObjectUsingGetQuery,
	useGetEnvironmentsWithLastDeploymentPerObjectTypeUsingGetQuery,
	useGetExistingStructuresForSourceMappingUsingGetQuery,
	useGetMappingsUsingGetQuery,
	useGetNodeDetailUsingGetQuery,
	useGetObjectSettingsByTypeUsingGetQuery,
	useGetReleaseEnvironmentsUsingGetQuery,
	useGetStereotypeTypesUsingGetQuery,
	useGetStereotypesForTemplatesUsingGetQuery,
	useGetStereotypesUsingGetQuery,
	useGetSystemReleaseUsingGetQuery,
	useGetSystemReleasesUsingGetQuery,
	useGetSystemUsersUsingGetQuery,
	useGetTemplateV1UsingGetQuery,
	useGenerateSqlPreviewUsingGetQuery,
	useGetUsersForComboUsingGetQuery,
	useLazyGenerateAndPushToGitUsingGetQuery,
	useLazyGetAvailableStructureForSourceObjectUsingGetQuery,
	useLazyGetNodeDetailUsingGetQuery,
	useLazyGetTaskExecutionUsingGetQuery,
	useLazySearchStructureUsingGetQuery,
	useSearchFullTextJsonUsingGetQuery,
	useUpdateSystemReleaseUsingPutMutation,
	useUpdateTemplateV1UsingPutMutation,
	useCancelTaskUsingDeleteMutation,
	useLazyGetTaskResultFileUsingGetQuery,
} = mddApi
